import { Drawer, Radio, Input, InputNumber, Select, Button, Space, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_STANDARDIZED_ADDRESS_FROM_API,
  AddressApiSource,
  CreateStandardizedAddressFromApiResponse,
  CreateStandardizedAddressFromApiVariables,
} from '../graphql/mutations/createStandardizedAddressFromApi';
import {
  CREATE_STANDARDIZED_ADDRESS_MANUALLY,
  CreateStandardizedAddressManuallyResponse,
  CreateStandardizedAddressManuallyVariables,
} from '../graphql/mutations/createStandardizedAddressManually';
import {
  GET_ALL_ZIPCODE_OPTIONS,
  GetAllZipcodeOptionsResponse,
} from '../graphql/queries/getZipcodeOptions';

type UiSource = AddressApiSource | 'MANUAL';

interface AddAddressFromApiDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddAddressFromApiDrawer: React.FC<AddAddressFromApiDrawerProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [source, setSource] = useState<UiSource>('SMARTY');
  const [rawJson, setRawJson] = useState('');
  // 手工维护表单字段
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateAbbr, setStateAbbr] = useState<string | undefined>(undefined);
  const [zipcode, setZipcode] = useState<string | undefined>(undefined);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);

  const [createAddress, { loading }] = useMutation<
    CreateStandardizedAddressFromApiResponse,
    CreateStandardizedAddressFromApiVariables
  >(CREATE_STANDARDIZED_ADDRESS_FROM_API);

  const [createAddressManually, { loading: manualLoading }] = useMutation<
    CreateStandardizedAddressManuallyResponse,
    CreateStandardizedAddressManuallyVariables
  >(CREATE_STANDARDIZED_ADDRESS_MANUALLY);

  // 切到手工维护时才拉取下拉数据，Apollo 缓存后续复用
  const { data: zipcodeData, loading: zipcodeLoading } = useQuery<GetAllZipcodeOptionsResponse>(
    GET_ALL_ZIPCODE_OPTIONS,
    { skip: source !== 'MANUAL', fetchPolicy: 'cache-first' },
  );

  const zipcodeOptions = useMemo(
    () =>
      (zipcodeData?.getAllZipcodeOptions ?? []).map((opt) => ({
        value: opt.zipcode,
        label: `${opt.zipcode} ${opt.city}, ${opt.state}`,
        city: opt.city,
        state: opt.state,
      })),
    [zipcodeData],
  );

  // 关闭时重置，避免下次打开沿用上次内容
  useEffect(() => {
    if (!visible) {
      setSource('SMARTY');
      setRawJson('');
      setStreet('');
      setCity('');
      setStateAbbr(undefined);
      setZipcode(undefined);
      setLatitude(null);
      setLongitude(null);
    }
  }, [visible]);

  const handleZipcodeChange = (value: string) => {
    setZipcode(value);
    // 选中邮编后反填城市/州（仍可手动修改）
    const selected = zipcodeOptions.find((opt) => opt.value === value);
    if (selected) {
      setCity(selected.city);
      setStateAbbr(selected.state);
    }
  };

  const handleManualSubmit = async () => {
    if (!street.trim()) {
      message.error('请输入街道信息');
      return;
    }
    if (!city.trim()) {
      message.error('请输入城市');
      return;
    }
    if (!stateAbbr) {
      message.error('请先选择邮编以带出州');
      return;
    }
    if (!zipcode || !/^\d{5}$/.test(zipcode)) {
      message.error('请选择5位数字邮编');
      return;
    }
    if (latitude === null || latitude < -90 || latitude > 90) {
      message.error('请输入合法纬度（-90 ~ 90）');
      return;
    }
    if (longitude === null || longitude < -180 || longitude > 180) {
      message.error('请输入合法经度（-180 ~ 180）');
      return;
    }

    try {
      const { data } = await createAddressManually({
        variables: {
          input: {
            street: street.trim(),
            city: city.trim(),
            state: stateAbbr,
            zipcode,
            latitude,
            longitude,
          },
        },
      });
      const result = data?.createStandardizedAddressManually;
      if (result?.success) {
        message.success(`添加成功，id=${result.addressId}`);
        onClose();
        onSuccess?.();
      } else {
        message.error(result?.errorMessage || '添加失败');
      }
    } catch (err) {
      message.error(`添加失败：${(err as Error).message}`);
    }
  };

  const handleApiSubmit = async () => {
    const trimmed = rawJson.trim();
    if (!trimmed) {
      message.error('请输入外部API响应JSON');
      return;
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      message.error('JSON格式不合法，请检查后重试');
      return;
    }

    // 来源与 JSON 结构一致性校验
    const obj = parsed as Record<string, unknown>;
    if (source === 'GOOGLE') {
      if (!obj || typeof obj !== 'object' || !Array.isArray(obj.results)) {
        message.error('所选数据源为 Google，但传入的不是 Google 响应格式');
        return;
      }
    } else {
      // SMARTY
      if (obj && typeof obj === 'object' && 'results' in obj) {
        message.error('所选数据源为 Smarty，但传入的疑似 Google 响应，请核对数据源');
        return;
      }
      const first = Array.isArray(parsed) ? (parsed[0] as Record<string, unknown> | undefined) : obj;
      if (!first || typeof first !== 'object' || !('components' in first)) {
        message.error('所选数据源为 Smarty，但传入的不是 Smarty 响应格式');
        return;
      }
    }

    try {
      const { data } = await createAddress({
        variables: { source: source as AddressApiSource, rawJson: trimmed },
      });
      const result = data?.createStandardizedAddressFromApi;
      if (result?.success) {
        message.success(`添加成功，id=${result.addressId}`);
        onClose();
        onSuccess?.();
      } else {
        message.error(result?.errorMessage || '添加失败');
      }
    } catch (err) {
      message.error(`添加失败：${(err as Error).message}`);
    }
  };

  const handleSubmit = () => {
    if (source === 'MANUAL') {
      handleManualSubmit();
    } else {
      handleApiSubmit();
    }
  };

  return (
    <Drawer
      title="添加标准化地址"
      open={visible}
      onClose={onClose}
      width={700}
      destroyOnHidden
      footer={
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button type="primary" loading={loading || manualLoading} onClick={handleSubmit}>
              确认
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ marginBottom: 8 }}>数据源：</div>
      <Radio.Group
        style={{ marginBottom: 16 }}
        value={source}
        onChange={(e) => setSource(e.target.value as UiSource)}
      >
        <Radio value="SMARTY">Smarty</Radio>
        <Radio value="GOOGLE">Google</Radio>
        <Radio value="MANUAL">手工维护</Radio>
      </Radio.Group>
      {source === 'MANUAL' ? (
        <>
          <div style={{ marginBottom: 8 }}>街道信息：</div>
          <Input
            style={{ marginBottom: 16 }}
            placeholder="如 123 Main St Apt 2（单元请写 Apt/Ste/Unit，勿用 #）"
            maxLength={200}
            value={street}
            onChange={(e) => setStreet(e.target.value)}
          />
          <div style={{ marginBottom: 8 }}>邮编：</div>
          <Select
            style={{ width: '100%', marginBottom: 16 }}
            showSearch
            placeholder="输入邮编或城市检索，选中后自动带出城市/州"
            loading={zipcodeLoading}
            options={zipcodeOptions}
            value={zipcode}
            onChange={handleZipcodeChange}
            filterOption={(input, option) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <div style={{ marginBottom: 8 }}>城市：</div>
          <Input
            style={{ marginBottom: 16 }}
            placeholder="选择邮编后自动带出，可修改"
            maxLength={100}
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
          <div style={{ marginBottom: 8 }}>州：</div>
          <Input
            style={{ marginBottom: 16 }}
            placeholder="选择邮编后自动带出"
            readOnly
            value={stateAbbr ?? ''}
          />
          <div style={{ marginBottom: 8 }}>纬度：</div>
          <InputNumber
            style={{ width: '100%', marginBottom: 16 }}
            placeholder="如 40.6301234（-90 ~ 90）"
            min={-90}
            max={90}
            step={0.000001}
            value={latitude}
            onChange={setLatitude}
          />
          <div style={{ marginBottom: 8 }}>经度：</div>
          <InputNumber
            style={{ width: '100%', marginBottom: 16 }}
            placeholder="如 -74.0201234（-180 ~ 180）"
            min={-180}
            max={180}
            step={0.000001}
            value={longitude}
            onChange={setLongitude}
          />
        </>
      ) : (
        <>
          <div style={{ marginBottom: 8 }}>响应JSON：</div>
          <Input.TextArea
            placeholder="粘贴 Smarty / Google 的原始 API 响应 JSON"
            autoSize={{ minRows: 12, maxRows: 24 }}
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
          />
        </>
      )}
    </Drawer>
  );
};

export default AddAddressFromApiDrawer;
